/**
 * The dashboard's Pause / Resume on a session row. Until 2026-09-30 both
 * buttons only showed "not yet wired — coming soon" (Ray found it on staging
 * MINIPC1 while testing a sub-vendo customer); Kick and +Time were the only
 * live controls. Kept apart from the page so the click can be tested.
 */
import { pauseSession, resumeSession } from '@/api/vendo';

type Kind = 'pause' | 'resume';

export interface SessionControlDeps {
   pause?: typeof pauseSession;
   resume?: typeof resumeSession;
   notify: { success: (msg: string) => void; error: (msg: string) => void };
   refetch: () => Promise<unknown> | unknown;
}

const SAID: Record<Kind, { doing: string; done: string; failed: string }> = {
   pause: { doing: 'Pausing…', done: 'Session paused', failed: 'Could not pause the session' },
   resume: { doing: 'Resuming…', done: 'Session resumed', failed: 'Could not resume the session' },
};

export function errorMessage(err: unknown, fallback: string): string {
   return (
      (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
      (err as Error)?.message ||
      fallback
   );
}

/** Run one control and tell the operator what happened. Resolves true on success. */
export async function runSessionControl(
   kind: Kind,
   id: string,
   deps: SessionControlDeps
): Promise<boolean> {
   const call = kind === 'pause' ? deps.pause ?? pauseSession : deps.resume ?? resumeSession;
   try {
      await call(id);
      deps.notify.success(SAID[kind].done);
      await deps.refetch();
      return true;
   } catch (err) {
      deps.notify.error(errorMessage(err, SAID[kind].failed));
      return false;
   }
}
